import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../context/AuthContext';
import { getMonthlyGoals } from '../../../firebase/services';

const formatMonthLabel = (monthStr) => {
  const [year, month] = monthStr.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

const getCompletionInfo = (entry) => {
  const totalGoal = Object.values(entry.goals || {}).reduce((sum, v) => sum + (v || 0), 0);
  const totalCount = Object.values(entry.counts || {}).reduce((sum, v) => sum + (v || 0), 0);
  if (totalGoal === 0) return { percent: 0, labelKey: 'goals.noGoalsSet', color: '#808080', totalGoal, totalCount };
  const percent = Math.round((totalCount / totalGoal) * 100);
  if (percent >= 100) return { percent: 100, labelKey: 'goals.allMet', color: '#4CAF50', totalGoal, totalCount };
  return { percent, label: percent + '%', color: '#E8A800', totalGoal, totalCount };
};

const PastGoalsScreen = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const [goalHistory, setGoalHistory] = useState([]);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const doc = await getMonthlyGoals(currentUser.uid);
        if (doc && Array.isArray(doc.goalHistory)) {
          const sorted = [...doc.goalHistory].sort((a, b) => b.month.localeCompare(a.month));
          setGoalHistory(sorted);
        }
      } catch (error) {
        console.error('PastGoalsScreen: Failed to fetch goal history:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [currentUser.uid]);

  return (
    <div style={{ backgroundColor: '#F8F8F8', minHeight: '100vh', padding: '16px', maxWidth: '400px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
        <span style={{ fontSize: '24px', cursor: 'pointer', marginRight: '12px' }} onClick={() => window.history.back()}>←</span>
        <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', fontWeight: 'bold', color: '#000033' }}>{t('goals.pastGoals')}</span>
      </div>

      {!loading && goalHistory.length === 0 && (
        <div style={{ textAlign: 'center', marginTop: '40px', padding: '0 20px' }}>
          <p style={{ fontSize: '14px', color: '#808080', lineHeight: 1.5 }}>
            {t('goals.noPastGoals')}
          </p>
        </div>
      )}

      {!loading && goalHistory.map((entry) => {
        const completionInfo = getCompletionInfo(entry);
        const { totalGoal, totalCount } = completionInfo;
        const isExpanded = expandedMonth === entry.month;

        return (
          <div key={entry.month} style={{ background: 'white', borderRadius: '10px', marginBottom: '12px', border: '1px solid #eee', overflow: 'hidden' }}>
            {/* Card header */}
            <div
              style={{ padding: '16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              onClick={() => setExpandedMonth(isExpanded ? null : entry.month)}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', color: '#000033' }}>
                  {formatMonthLabel(entry.month)}
                </div>
                <div style={{ fontSize: '12px', color: '#808080', marginTop: '2px' }}>
                  {totalGoal > 0
                    ? t('goals.activitiesCompleted', { count: totalCount, total: totalGoal })
                    : t('goals.noGoalsWereSet')}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {totalGoal > 0 && (
                  <span style={{
                    fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px',
                    background: completionInfo.color + '26', color: completionInfo.color,
                  }}>
                    {completionInfo.labelKey ? t(completionInfo.labelKey) : completionInfo.label}
                  </span>
                )}
                <span style={{ fontSize: '14px', color: '#808080', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', display: 'inline-block' }}>
                  ›
                </span>
              </div>
            </div>

            {/* Expanded detail */}
            {isExpanded && (
              <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f0f0f0' }}>
                {Object.keys(entry.goals || {}).map((causeTitle) => {
                  const goal = entry.goals[causeTitle];
                  const count = (entry.counts || {})[causeTitle] || 0;
                  const isMet = goal > 0 && count >= goal;
                  const fillPercent = goal > 0 ? Math.min(100, (count / goal) * 100) : 0;
                  const barColor = isMet ? '#4CAF50' : '#FFD100';

                  return (
                    <div key={causeTitle} style={{ paddingTop: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '13px', color: '#000033' }}>{causeTitle}</span>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: isMet ? '#4CAF50' : '#000033' }}>
                          {count} / {goal}
                        </span>
                      </div>
                      <div style={{ width: '100%', height: '4px', background: '#E0E0E0', borderRadius: '2px', overflow: 'hidden', marginTop: '4px' }}>
                        <div style={{ width: `${fillPercent}%`, height: '100%', background: barColor, borderRadius: '2px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PastGoalsScreen;
